import React from 'react';
import moment from 'moment'
import './calendar-carousel.scss'
import FontAwesome from 'react-fontawesome'
import { breakpoints } from '@constants'

const DESKTOP_DAY_WIDTH = 80;
const DESKTOP_ARROW_COUNT = 2;
const MIN_DAYS_SMALL_SCREEN = 5;
const MIN_DAYS_LARGE_SCREEN = 3;
const SWIPE_DAY_GAP = 5;
const MIN_DAY_WIDTH = 45;
const SWIPE_THRESHOLD_DIVISOR = 3;
const SWIPE_TRANSITION_DURATION = 0.3;
const DATE_LOCALE = 'pt-BR';
const TRIP_DATE_FORMAT = 'YYYY-MM-DD';

class CalendarCarousel extends React.Component {
    constructor(parameters) {
        super(parameters);
        this.carouselContainerRef = React.createRef();
        this.resizeObserver = null;
        this.dayContainerEl = null;
        this.setDayContainerRef = (el) => {
            this.dayContainerEl = el;
            if (el) requestAnimationFrame(() => this.updateDayContainerWidth());
        };
        this.state = {
            page: 0,
            centerDate: this.props.selectedDate ? new Date(this.props.selectedDate) : new Date(),
            containerWidth: 0,
            dayContainerWidth: DESKTOP_DAY_WIDTH,
            swipeOffset: 0,
            touchStartX: 0,
            isTransitioning: false,
            pendingPage: null
        };
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    }

    componentDidMount() {
        if (this.carouselContainerRef.current) {
            this.resizeObserver = new ResizeObserver(entries => {
                const width = entries[0].contentRect.width;
                requestAnimationFrame(() => {
                    this.setState(prev => prev.containerWidth !== width ? { containerWidth: width } : null);
                    this.updateDayContainerWidth();
                });
            });
            this.resizeObserver.observe(this.carouselContainerRef.current);
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    updateDayContainerWidth() {

        if (!this.dayContainerEl) return;

        const width = this.dayContainerEl.offsetWidth;

        if (width > 0) {
            this.setState(prev => prev.dayContainerWidth !== width ? { dayContainerWidth: width } : null);
        }
    }

    calculateAmountOfDaysToShow() {
        const { containerWidth } = this.state;
        const isSmallScreen = containerWidth <= Number(breakpoints.MEDIUM_SCREEN);

        if (!isSmallScreen) {
            let n = Math.floor(containerWidth / DESKTOP_DAY_WIDTH - DESKTOP_ARROW_COUNT);
            if (n % 2 === 0) n--;
            return Math.max(MIN_DAYS_LARGE_SCREEN, n);
        }

        const maxFitting = Math.floor(
            (containerWidth - MIN_DAY_WIDTH - SWIPE_DAY_GAP) / (MIN_DAY_WIDTH + SWIPE_DAY_GAP)
        );

        const maxFittingOdd = maxFitting % 2 === 0 ? maxFitting - 1 : maxFitting;

        let n = Math.min(Math.floor(containerWidth / DESKTOP_DAY_WIDTH), maxFitting);

        if (n % 2 === 0) n--;

        const minAllowed = Math.min(MIN_DAYS_SMALL_SCREEN, Math.max(1, maxFittingOdd));
        return Math.max(minAllowed, n);
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.selectedDate &&
            (!prevProps.selectedDate ||
                new Date(this.props.selectedDate).getTime() !==
                new Date(prevProps.selectedDate).getTime()
            )
        ) {
            const newCenterDate = new Date(this.props.selectedDate);

            if (this.state.centerDate.getTime() !== newCenterDate.getTime() || this.state.page !== 0) {
                this.setState({ centerDate: newCenterDate, page: 0 });
            }
        }
    }

    generateDaysArray(amount, pageOffset = this.state.page) {
        const days = [];
        const locale = DATE_LOCALE;
        const referenceDate = new Date(this.state.centerDate);

        referenceDate.setHours(0, 0, 0, 0);
        referenceDate.setDate(referenceDate.getDate() + pageOffset * amount);

        const half = Math.floor(amount / 2);

        const selectedDateNormalized = new Date(this.state.centerDate);
        selectedDateNormalized.setHours(0, 0, 0, 0);
        const selectedTime = selectedDateNormalized.getTime();

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { limitDate, isGoing } = this.props;
        const parsedLimit = limitDate ? new Date(limitDate) : null;
        const limitTime = parsedLimit && !isNaN(parsedLimit.getTime())
            ? new Date(parsedLimit).setHours(0, 0, 0, 0)
            : null;

        for (let i = 0; i < amount; i++) {
            const day = new Date(referenceDate);

            day.setDate(referenceDate.getDate() + i - half);
            day.setHours(0, 0, 0, 0);

            const dayName = day.toLocaleDateString(locale, { weekday: 'short' });
            const monthName = day.toLocaleDateString(locale, { month: 'short' });
            const dayNumber = day.getDate();
            const isToday = day.getTime() === today.getTime();
            const isPrevious = day.getTime() < today.getTime();
            const isSelected = day.getTime() === selectedTime;
            const isLimitDisabled = limitTime !== null && (
                isGoing ? day.getTime() > limitTime : day.getTime() < limitTime
            );

            days.push({ dayName, monthName, dayNumber, isPrevious, isToday, isSelected, isLimitDisabled, date: new Date(day) });
        }

        return days;
    }

    pageHasValidDays(amount, pageOffset) {
        return this.generateDaysArray(amount, pageOffset).some(d => !d.isPrevious);
    }


    handleTouchStart(e) {
        this.setState({ touchStartX: e.touches[0].clientX, swipeOffset: 0 });
    }

    handleTouchMove(e) {
        if (this.state.isTransitioning) return;
        const delta = e.touches[0].clientX - this.state.touchStartX;
        if (delta > 0 && !this.pageHasValidDays(this.calculateAmountOfDaysToShow(), this.state.page - 1)) return;
        this.setState({ swipeOffset: delta });
    }

    getSwipePageWidth() {
        const { containerWidth } = this.state;
        const amount = this.calculateAmountOfDaysToShow();
        const dayW = Math.max(MIN_DAY_WIDTH, containerWidth / (amount + 1) - SWIPE_DAY_GAP);
        return containerWidth - dayW;
    }

    handleTouchEnd() {
        if (this.state.isTransitioning) return;

        const { swipeOffset, page } = this.state;
        const pageWidth = this.getSwipePageWidth();
        const amount = this.calculateAmountOfDaysToShow();
        const threshold = pageWidth / SWIPE_THRESHOLD_DIVISOR;

        if (swipeOffset < -threshold) {
            this.setState({ isTransitioning: true, pendingPage: page + 1, swipeOffset: -pageWidth });
        } else if (swipeOffset > threshold && this.pageHasValidDays(amount, page - 1)) {
            this.setState({ isTransitioning: true, pendingPage: page - 1, swipeOffset: pageWidth });
        } else if (swipeOffset !== 0) {
            this.setState({ isTransitioning: true, pendingPage: null, swipeOffset: 0 });
        }
    }

    handleTransitionEnd() {
        const { pendingPage } = this.state;

        if (pendingPage !== null) {
            this.setState({ page: pendingPage, pendingPage: null, swipeOffset: 0, isTransitioning: false });
        } else {
            this.setState({ isTransitioning: false });
        }
    }

    renderDayButton(day, i, attachRef, style) {
        const isDisabled = day.isPrevious || day.isLimitDisabled;
        return (
            <button
                key={day.date.getTime()}
                ref={attachRef ? this.setDayContainerRef : null}
                style={style}
                onClick={() => {
                    if (this.props.searchTrips && !isDisabled) {
                        const tripDateSearch = moment(day.date).format(TRIP_DATE_FORMAT)

                        if (!this.props.origin || !this.props.destination) {
                            this.props.toastWarning({ message: 'Escolha origem e destino' })
                            return
                        }

                        this.props.searchTrips({ tripDateSearch, isGoing: this.props.isGoing })
                    }
                }}
                className={`
                    day-container
                    ${isDisabled ? 'previous-day-container' : ''}
                    ${!isDisabled ? 'able-day-container' : ''}
                    ${day.isSelected ? 'day-container-selected' : ''}`}
            >
                <span className='day-name'>{day.dayName}</span>
                <span className='day-number'>{day.dayNumber}</span>
                <span className='month-name'>{day.monthName}</span>
            </button>
        );
    }

    render() {
        const amountOfDaysToShow = this.calculateAmountOfDaysToShow();
        const { containerWidth, swipeOffset, isTransitioning, page } = this.state;
        const isSmallScreen = containerWidth > 0 && containerWidth <= Number(breakpoints.MEDIUM_SCREEN);

        if (isSmallScreen) {
            const dayW = Math.max(MIN_DAY_WIDTH, containerWidth / (amountOfDaysToShow + 1) - SWIPE_DAY_GAP);
            const pageWidth = containerWidth - dayW;
            const baseOffset = -(pageWidth - dayW / 2);
            const dayStyle = { flex: `0 0 ${dayW}px`, width: `${dayW}px`, minWidth: `${dayW}px` };

            const prevDays = this.generateDaysArray(amountOfDaysToShow, page - 1);
            const currentDays = this.generateDaysArray(amountOfDaysToShow, page);
            const nextDays = this.generateDaysArray(amountOfDaysToShow, page + 1);

            const swipePageStyle = {
                display: 'flex',
                alignItems: 'center',
                gap: `${SWIPE_DAY_GAP}px`,
                padding: `0 ${SWIPE_DAY_GAP}px`,
                boxSizing: 'border-box',
                width: `${pageWidth}px`,
                minWidth: `${pageWidth}px`,
                flexShrink: 0,
            };

            return (
                <div
                    className='calendar-carousel-container'
                    ref={this.carouselContainerRef}
                    style={{ overflow: 'hidden' }}
                    onTouchStart={this.handleTouchStart}
                    onTouchMove={this.handleTouchMove}
                    onTouchEnd={this.handleTouchEnd}
                >
                    <div
                        style={{
                            display: 'flex',
                            transform: `translateX(${baseOffset + swipeOffset}px)`,
                            transition: isTransitioning ? `transform ${SWIPE_TRANSITION_DURATION}s ease` : 'none',
                            willChange: 'transform'
                        }}
                        onTransitionEnd={this.handleTransitionEnd}
                    >
                        <div style={swipePageStyle}>
                            {prevDays.map((day, i) => this.renderDayButton(day, i, false, dayStyle))}
                        </div>
                        <div style={swipePageStyle}>
                            {currentDays.map((day, i) => this.renderDayButton(day, i, i === 0, dayStyle))}
                        </div>
                        <div style={swipePageStyle}>
                            {nextDays.map((day, i) => this.renderDayButton(day, i, false, dayStyle))}
                        </div>
                    </div>
                </div>
            );
        }

        const daysToShow = this.generateDaysArray(amountOfDaysToShow);
        const hasToday = daysToShow.some(d => d.isToday);

        return (
            <div className='calendar-carousel-container' ref={this.carouselContainerRef}>
                <button
                    className={`arrow-container ${hasToday ? 'disabled' : ''}`}
                    disabled={hasToday}
                    onClick={() => { this.setState(prev => ({ page: prev.page - 1 })) }}
                >
                    <FontAwesome
                        name="angle-left"
                        title="Dias anteriores"
                        id="arrow-left"
                    />
                </button>
                {daysToShow.map((day, i) => this.renderDayButton(day, i, i === 0))}
                <button
                    className='arrow-container'
                    onClick={() => { this.setState(prev => ({ page: prev.page + 1 })) }}
                >
                    <FontAwesome
                        name="angle-right"
                        title="Dias afrente"
                        id="arrow-right"
                    />
                </button>
            </div>
        );
    }
}

export default CalendarCarousel
